import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { BUTTON, WHITE_BUTTON } from "portal/theme/theme";
import { getJobPath } from "portal/utils/routing";
import { Job } from "protos/rtc/jobs";
import { JobType } from "protos/atk/job_type";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { titleCase } from "portal/utils/strings";
import { useMutationPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import React, { FC, ReactNode, useState } from "react";

/**
 * Suggested name for a clone. The jobs list groups rows by name, so a clone
 * that kept the source's name would be folded into the source's group and look
 * like nothing happened. Suggest something distinct instead.
 */
function suggestedName(job: Job): string {
  return `${job.name} (copy)`;
}

const CloneJobDialogContent: FC<{
  job: Job;
  isOpen: boolean;
  close: () => void;
}> = ({ job, isOpen, close }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Remounted per open via the `key` below, so this initializes from the job
  // rather than needing an effect to resync.
  const [name, setName] = useState<string>(() => suggestedName(job));
  const [cloneJob, { isLoading }] = useMutationPopups(
    rtcJobsApi.endpoints.cloneJob.useMutation()
  );

  const trimmedName = name.trim();
  const title = t("views.autotractor.jobDetails.cloneJob");
  const isGroundPrep = job.type === JobType.GROUND_PREP;

  async function submit(): Promise<void> {
    const result = await cloneJob({
      id: job.id.toString(),
      request: { name: trimmedName },
    });
    if (result.error === undefined) {
      close();
      const clonedJobId = result.data.job?.id;
      if (clonedJobId !== undefined) {
        navigate(getJobPath(clonedJobId.toString()));
      }
    }
  }

  return (
    // Not dismissable while the clone is in flight: the request can run for
    // minutes and the note below tells the user not to navigate away.
    <Dialog
      open={isOpen}
      onClose={isLoading ? undefined : close}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          id="clone-job-name"
          label={titleCase(t("utils.descriptors.name"))}
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={isLoading}
        />
        {isGroundPrep ? (
          // Ground prep regenerates objectives through the path-planning
          // lambda, so this request is slow. The other job types return fast.
          <Typography variant="body2" className="mt-4">
            {t("views.autotractor.jobDetails.cloneJobSlowNote")}
          </Typography>
        ) : undefined}
      </DialogContent>
      <DialogActions>
        <Button {...WHITE_BUTTON} onClick={close} disabled={isLoading}>
          {t("utils.actions.cancel")}
        </Button>
        <Button
          {...BUTTON}
          disabled={trimmedName.length === 0 || isLoading}
          onClick={submit}
          startIcon={
            isLoading ? (
              <CircularProgress variant="indeterminate" size="1rem" />
            ) : undefined
          }
        >
          {t("utils.actions.clone")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Dialog for cloning a job, following the same shape as useEditJobDialog.
 *
 * The clone copies the job's configuration and robot whitelist and regenerates
 * its objectives; for ground prep that re-runs path planning, so the request
 * can take a while. On success we navigate to the new job.
 */
export function useCloneJobDialog(job: Job): {
  renderDialog: () => ReactNode;
  open: () => void;
  close: () => void;
} {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  // Bumped on each open so the content remounts and re-derives the suggested
  // name from the job's current name.
  const [openCount, setOpenCount] = useState<number>(0);

  function close(): void {
    setIsOpen(false);
  }

  return {
    renderDialog: () => (
      <CloneJobDialogContent
        key={openCount}
        job={job}
        isOpen={isOpen}
        close={close}
      />
    ),
    open: () => {
      setOpenCount((count) => count + 1);
      setIsOpen(true);
    },
    close,
  };
}
